import {diApi} from '@di-shared/api/di';
import {
  CreatedMaintenanceRecordRequest,
  GetMaintenanceRecordResponse,
  MaintenanceNotificationWithRecordResponse,
} from '@di-shared/api/di/__generated';
import {emitEventLog} from '@di-shared/features/analytics';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import useApiError from '@di-shared/features/common/useApiError';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useSestExchangeHistorySelectionStep, {
  ManageMethod,
  SelectionStep,
} from '@di-shared/features/diag/exchange/history/bottom-sheet/auto/useSestExchangeHistorySelectionStep';
import useSestCodeToName from '@di-shared/features/diag/sest/useSestCodeToName';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import PreventForm from '@shared/PreventForm';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {useQueryClient} from '@tanstack/react-query';

function getAction({
  manageMethod,
  records,
  selectedRecord,
}: {
  manageMethod: ManageMethod;
  records: [GetMaintenanceRecordResponse, GetMaintenanceRecordResponse];
  selectedRecord: number;
}) {
  if (manageMethod === ManageMethod.ANOTHER) {
    return MaintenanceNotificationWithRecordResponse.actionTaken.KEEP_BOTH;
  }
  if (getRecentRecordIndex(records) === selectedRecord) {
    return MaintenanceNotificationWithRecordResponse.actionTaken.KEEP_NEW_RECORD;
  }
  return MaintenanceNotificationWithRecordResponse.actionTaken.KEEP_EXIST_RECORD;
}

function getRecentRecordIndex(records: [GetMaintenanceRecordResponse, GetMaintenanceRecordResponse]) {
  const firstRecordReplaceDate = parseDateWithFormat(records[0].replaceDate);
  const secondRecordReplaceDate = parseDateWithFormat(records[1].replaceDate);
  if (!firstRecordReplaceDate || !secondRecordReplaceDate) {
    throw new ExpectedError('Record replaceDate not found');
  }

  return firstRecordReplaceDate >= secondRecordReplaceDate ? 0 : 1;
}

interface SestExchangeHistorySelectionProps {
  onNext: () => void;
  vin: string;
  records: [GetMaintenanceRecordResponse, GetMaintenanceRecordResponse];
  notificationId: number;
}

function SestExchangeHistorySelection({onNext, vin, records, notificationId}: SestExchangeHistorySelectionProps) {
  const queryClient = useQueryClient();
  const {resolveError} = useApiError();
  const {convertSestCodesToNames} = useSestCodeToName();
  const {selectionStep, goToConfirmStep, manageMethod, selectedRecord, manageAnotherRecord, manageSameRecord, updateSelectedRecord} =
    useSestExchangeHistorySelectionStep();

  const [record] = records;

  const [submitSelection] = usePendingHandler(
    async () => {
      if (selectedRecord === null || manageMethod === null) {
        return;
      }
      const action = getAction({records, selectedRecord, manageMethod});
      await diApi.maintenanceNotificationController.updateNotification2({
        vin,
        notificationId,
        action,
      });
      queryClient.invalidateQueries({queryKey: [getQueryKey(diApi.maintenanceNotificationController.getUnreadUserNotifications)]});
      queryClient.invalidateQueries({queryKey: [getQueryKey(diApi.maintenancesSestController.getSest)]});
      onNext();
    },
    async (error) => {
      await resolveError(error);
    }
  );

  return (
    <div className="bottom_sheet">
      <div className="bottom_sheet_header">
        <h2 className="bottom_sheet_title">{convertSestCodesToNames([record.sestCode as CreatedMaintenanceRecordRequest.sestCode])}</h2>
      </div>
      <PreventForm onSubmit={() => submitSelection()}>
        {selectionStep === SelectionStep.MAIN && (
          <SestExchangeHistorySelectionMain
            records={records}
            onClickManageAnotherRecord={() => manageAnotherRecord(getRecentRecordIndex(records))}
            onClickManageSameRecord={manageSameRecord}
          />
        )}
        {selectionStep === SelectionStep.RADIO && (
          <SestExchangeHistorySelectionRadio
            records={records}
            selectedRecord={selectedRecord}
            onClickConfirm={goToConfirmStep}
            updateSelectedRecord={(selectedRecordIndex) => updateSelectedRecord(selectedRecordIndex)}
          />
        )}
        {selectionStep === SelectionStep.CONFIRM && selectedRecord !== null && manageMethod !== null && (
          <SestExchangeHistorySelectionConfirm
            records={records}
            selectedRecord={selectedRecord}
            manageMethod={manageMethod}
          />
        )}
      </PreventForm>
    </div>
  );
}

export default SestExchangeHistorySelection;

interface SestExchangeHistorySelectionMainProps {
  records: [GetMaintenanceRecordResponse, GetMaintenanceRecordResponse];
  onClickManageSameRecord: () => void;
  onClickManageAnotherRecord: () => void;
}

function SestExchangeHistorySelectionMain({
  records,
  onClickManageSameRecord,
  onClickManageAnotherRecord,
}: SestExchangeHistorySelectionMainProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  return (
    <div className="bottom_sheet_inner">
      <div className="bottom_sheet_contents">
        <strong
          className="bottom_sheet_title_sub"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30295')}
        </strong>

        <div className="history_confirm">
          <ul className="history_confirm_list">
            {records.map(({replaceDate, replaceOdometer, id, source}) => {
              const parsedReplaceDate = parseDateWithFormat(replaceDate);
              return (
                <li
                  className="history_confirm_item"
                  key={id}
                >
                  <span className="history_confirm_date">
                    {parsedReplaceDate && CldrDatetimeFormatter.format(parsedReplaceDate, 'yMMMd')}&nbsp;{t('T30296')}
                    <div className="badge_box">
                      <span className="badge badge_basic_container badge_20px">
                        {source === GetMaintenanceRecordResponse.source.USER ? t('T30300') : t('T30301')}
                      </span>
                    </div>
                  </span>
                  <span className="history_confirm_text">
                    {t('T30152')}&nbsp;:&nbsp;{formatNumberWithCommas(convertDistanceValue(replaceOdometer))}
                    {convertDistanceSymbol(replaceOdometer)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="bottom_sheet_footer bottom_sheet_footer_vertical">
        <button
          type="button"
          className="btn btn_large btn_filled"
          onClick={() => {
            emitEventLog({
              event: 'consumables_exchange_update',
              form_step: 'confirm validation',
              click_text: t('T30297'),
              click_location: 'body',
            });
            onClickManageSameRecord();
          }}
        >
          {t('T30297')}
        </button>

        <button
          type="button"
          className="btn btn_large btn_outline"
          onClick={() => {
            emitEventLog({
              event: 'consumables_exchange_update',
              form_step: 'reject validation',
              click_text: t('T30298'),
              click_location: 'body',
            });
            onClickManageAnotherRecord();
          }}
        >
          {t('T30298')}
        </button>
      </div>
    </div>
  );
}

interface SestExchangeHistorySelectionRadioProps {
  records: [GetMaintenanceRecordResponse, GetMaintenanceRecordResponse];

  selectedRecord: number | null;
  updateSelectedRecord: (selectedRecordIndex: number) => void;
  onClickConfirm: () => void;
}

function SestExchangeHistorySelectionRadio({
  records,
  selectedRecord,
  onClickConfirm,
  updateSelectedRecord,
}: SestExchangeHistorySelectionRadioProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  return (
    <div className="bottom_sheet_inner">
      <div className="bottom_sheet_contents">
        <strong className="bottom_sheet_title_sub">{t('T30299')}</strong>

        <div className="history_confirm">
          {records.map(({replaceDate, replaceOdometer, id, source}, index) => {
            const parseReplaceDate = parseDateWithFormat(replaceDate);
            return (
              <div
                className="input_box input_box_outline"
                key={id}
              >
                <input
                  type="radio"
                  name="historyConfirm"
                  id={id.toString()}
                  className="input"
                  checked={selectedRecord === index}
                  onChange={() => updateSelectedRecord(index)}
                />
                <label
                  htmlFor={id.toString()}
                  className="label"
                >
                  <span className="history_confirm_date">
                    {parseReplaceDate && CldrDatetimeFormatter.format(parseReplaceDate, 'yMMMd')}&nbsp;{t('T30296')}
                    <div className="badge_box">
                      <span className="badge badge_basic_container badge_20px">
                        {source === GetMaintenanceRecordResponse.source.USER ? t('T30300') : t('T30301')}
                      </span>
                    </div>
                  </span>
                  <span className="history_confirm_text">
                    {t('T30152')}&nbsp;:&nbsp;{formatNumberWithCommas(convertDistanceValue(replaceOdometer))}
                    {convertDistanceSymbol(replaceOdometer)}
                  </span>
                </label>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bottom_sheet_footer">
        <button
          type="button"
          className="btn btn_large btn_filled"
          onClick={() => {
            emitEventLog({
              event: 'consumables_exchange_update',
              form_step: 'select from the validation list',
              click_text: t('T10114'),
              click_location: 'body',
            });
            onClickConfirm();
          }}
          disabled={selectedRecord === null}
        >
          {t('T10114')}
        </button>
      </div>
    </div>
  );
}

interface SestExchangeHistorySelectionConfirmProps {
  manageMethod: ManageMethod;
  records: [GetMaintenanceRecordResponse, GetMaintenanceRecordResponse];
  selectedRecord: number;
}

function SestExchangeHistorySelectionConfirm({manageMethod, records, selectedRecord}: SestExchangeHistorySelectionConfirmProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const parsedReplaceDate = parseDateWithFormat(records[selectedRecord].replaceDate);
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  return (
    <div className="bottom_sheet_inner">
      <div className="bottom_sheet_contents">
        <strong className="bottom_sheet_title_sub">
          {manageMethod === ManageMethod.ANOTHER
            ? t('T30302')
            : records[selectedRecord].source === GetMaintenanceRecordResponse.source.USER
              ? t('T30303')
              : t('T30304')}
          &nbsp;{t('T30305')}
        </strong>
        <div className="history_confirm">
          <div className="history_confirm_item">
            <span className="history_confirm_date">
              {parsedReplaceDate && CldrDatetimeFormatter.format(parsedReplaceDate, 'yMMMd')}&nbsp;{t('T30296')}
              <div className="badge_box">
                <span className="badge badge_basic_container badge_20px">
                  {records[selectedRecord].source === GetMaintenanceRecordResponse.source.USER ? t('T30300') : t('T30301')}
                </span>
              </div>
            </span>
            <span className="history_confirm_text">
              {t('T30152')}&nbsp;:&nbsp;{formatNumberWithCommas(convertDistanceValue(records[selectedRecord].replaceOdometer))}
              {convertDistanceSymbol(records[selectedRecord].replaceOdometer)}
            </span>
          </div>
        </div>
      </div>
      <div className="bottom_sheet_footer">
        <button
          type="submit"
          className="btn btn_large btn_filled"
        >
          {t('T10114')}
        </button>
      </div>
    </div>
  );
}
